import React from "react";
import "./Disclaimer.css";
import { useLocalContext } from "../../context/LocalContext";
import {
  FaInfoCircle,
  FaShieldAlt,
  FaExternalLinkAlt,
  FaPenNib,
  FaRegCheckCircle,
  FaGavel,
} from "react-icons/fa";

const Disclaimer = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = (webinfo.name || "Our Company").trim();
  const email = (webinfo.email || "").trim();
  const phone = (webinfo.phone || "").trim();
  const phoneCall = (webinfo.phonecall || webinfo.phoneCall || "").trim();

  const updatedOn = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

  const highlights = [
    "We don’t guarantee rankings, revenue, leads, or ROI.",
    "Third-party platforms (Google/Meta/tools) can change without notice.",
    "You remain responsible for final approvals and business decisions.",
    "External links/tools are provided “as is” without warranties.",
  ];

  const sections = [
    {
      icon: <FaPenNib />,
      title: "Information on this website",
      body: (
        <>
          Content on this website is provided for general information purposes only.
          We try to keep information accurate and current, but <b>{brand}</b> does
          not make warranties regarding completeness, reliability, or accuracy.
        </>
      ),
    },
    {
      icon: <FaShieldAlt />,
      title: "No professional advice",
      body: (
        <>
          Nothing on this website constitutes legal, financial, medical, or other
          professional advice. You should consult qualified professionals before
          making decisions based on any information or materials available here.
        </>
      ),
    },
    {
      icon: <FaInfoCircle />,
      title: "No guarantees of results",
      body: (
        <>
          Digital outcomes vary based on factors outside our control (industry,
          competition, budgets, platform policies, website health, product-market fit,
          and changes by third parties). Therefore, <b>{brand}</b> does not guarantee
          specific outcomes such as rankings, conversions, sales, leads, or ROI.
        </>
      ),
    },
    {
      icon: <FaRegCheckCircle />,
      title: "Deliverables vs outcomes",
      body: (
        <>
          When you work with us, we commit to agreed deliverables (e.g., pages, designs,
          implementations, tracking setup, optimization tasks). However, deliverables
          do not equal guaranteed results. Performance depends on execution, market
          conditions, and factors outside the scope of our control.
        </>
      ),
    },
    {
      icon: <FaExternalLinkAlt />,
      title: "External links & third-party tools",
      body: (
        <>
          This website may contain links to third-party sites, services, or tools.
          These links are provided for convenience only. <b>{brand}</b> does not
          control third-party content and is not responsible for their availability,
          policies, accuracy, or security practices.
        </>
      ),
    },
    {
      icon: <FaShieldAlt />,
      title: "Platforms, ads, and policy changes",
      body: (
        <>
          Advertising and analytics depend on platforms such as Google, Meta, and
          other providers. These platforms may change algorithms, review policies,
          tracking rules, pricing, or availability at any time. Such changes can
          affect campaign performance and tracking accuracy.
        </>
      ),
    },
    {
      icon: <FaShieldAlt />,
      title: "Client responsibilities",
      body: (
        <>
          You are responsible for providing accurate inputs (business details, access,
          approvals, assets, and compliance information). You remain responsible for
          final review and approval of creatives, copy, claims, and business decisions.
        </>
      ),
    },
    {
      icon: <FaShieldAlt />,
      title: "Limitation of liability",
      body: (
        <>
          To the maximum extent permitted by law, <b>{brand}</b> will not be liable
          for any direct, indirect, incidental, consequential, or special damages
          arising from your use of this website or reliance on its content, links,
          or services.
        </>
      ),
    },
    {
      icon: <FaPenNib />,
      title: "Intellectual property",
      body: (
        <>
          Unless stated otherwise, all content on this website (text, visuals, layouts,
          and brand assets) is owned by <b>{brand}</b> or licensed for use. Unauthorized
          reproduction, redistribution, or reuse may be prohibited.
        </>
      ),
    },
    {
      icon: <FaGavel />,
      title: "Updates & interpretation",
      body: (
        <>
          We may update this disclaimer from time to time without prior notice.
          Continued use of the website indicates acceptance of the updated disclaimer.
          If any portion is found unenforceable, the remaining sections will continue
          in effect to the fullest extent permitted by law.
        </>
      ),
    },
  ];

  return (
    <section className="dsc">
      {/* HERO */}
      <header className="dsc__hero">
        <div className="dsc__heroTop">
          <div className="dsc__heroIcon" aria-hidden="true">
            <FaInfoCircle />
          </div>

          <div className="dsc__heroText">
            <span className="dsc__badge">Policy</span>
            <h1>Disclaimer</h1>
            <p>
              This disclaimer explains important limitations and responsibilities
              related to information and services provided by <b>{brand}</b>. By
              using this website, you agree to the terms below.
            </p>

            <div className="dsc__meta">
              <span>Last updated: <b>{updatedOn}</b></span>
              <span className="dot" />
              <span>Applies to: Website & digital services</span>
            </div>
          </div>
        </div>

        <div className="dsc__quick">
          <h3>Quick highlights</h3>
          <ul>
            {highlights.map((h, i) => (
              <li key={i}>
                <FaRegCheckCircle /> <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>

        <span className="dsc__glow dsc__glow1" aria-hidden="true" />
        <span className="dsc__glow dsc__glow2" aria-hidden="true" />
      </header>

      {/* CONTENT */}
      <div className="dsc__wrap">
        <div className="dsc__grid">
          {sections.map((s, i) => (
            <article className="dscCard" key={i}>
              <div className="dscCard__top">
                <div className="dscCard__ico">{s.icon}</div>
                <div className="dscCard__title">
                  <span className="num">{String(i + 1).padStart(2, "0")}</span>
                  <h2>{s.title}</h2>
                </div>
              </div>
              <p className="dscCard__body">{s.body}</p>
            </article>
          ))}
        </div>

        {/* CONTACT */}
        <div className="dscContact">
          <div className="dscContact__left">
            <h3>Need clarification?</h3>
            <p className="muted">
              If you have questions about this disclaimer, contact us and we’ll help.
            </p>
          </div>

          <div className="dscContact__right">
            {email ? (
              <a className="dscChip" href={`mailto:${email}`}>
                {email}
              </a>
            ) : (
              <span className="muted">Add email in webinfo</span>
            )}

            {phone ? (
              <a className="dscChip" href={phoneCall ? `tel:${phoneCall}` : "#"}>
                {phone}
              </a>
            ) : (
              <span className="muted">Add phone in webinfo</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Disclaimer;